import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePortalAuth } from './PortalAuthContext';
import { Button, Input } from '../design-system';
import './Portal.css';

// Serves two jobs: the FORCED change after a firm-issued temporary password,
// and a voluntary change later. The copy adapts, but the flow is identical —
// one screen rather than two nearly-identical ones.
function PortalSetPassword() {
  const { user, changePassword, logout } = usePortalAuth();
  const navigate = useNavigate();
  const forced = !!user?.mustChangePassword;

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    // Caught here so a typo doesn't cost a round trip; the server enforces the
    // length rule regardless.
    if (next !== confirm) {
      setError('The two new passwords do not match');
      return;
    }
    if (next.length < 10) {
      setError('Please use at least 10 characters');
      return;
    }
    setBusy(true);
    try {
      await changePassword(current, next);
      navigate('/portal', { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || 'Could not change your password');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="portal-auth-page">
      <form className="portal-auth-card" onSubmit={submit}>
        <h1 className="portal-auth-title">
          {forced ? 'Choose a password' : 'Change password'}
        </h1>
        <p className="portal-auth-sub">
          {forced
            ? 'Your accountant gave you a temporary password. Please pick your own before continuing.'
            : 'Signing in again on your other devices will be required after this.'}
        </p>

        {error && <div className="portal-auth-error" role="alert">{error}</div>}

        <Input
          label={forced ? 'Temporary password' : 'Current password'}
          type="password"
          autoComplete="current-password"
          autoFocus
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
        />
        <Input
          label="New password"
          type="password"
          autoComplete="new-password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          helperText="At least 10 characters. A short phrase works well."
        />
        <Input
          label="Confirm new password"
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
        />

        <Button type="submit" variant="primary" loading={busy} disabled={!current || !next || !confirm}>
          Save password
        </Button>

        {/* A forced change has no other way out, so signing out has to be
            reachable — otherwise a client who can't find the temporary password
            is stuck on this screen. */}
        {forced && (
          <button
            type="button"
            className="portal-auth-link"
            onClick={async () => { await logout(); navigate('/portal/login', { replace: true }); }}
          >
            Sign out instead
          </button>
        )}
      </form>
    </div>
  );
}

export default PortalSetPassword;
